import { Badge } from '@/components/ui/badge';
import { useNeedsAttention } from '@/features/needs-attention/use-needs-attention';
import { cn } from '@/lib/utils';

/**
 * The persistent count in the sidebar.
 *
 * The dashboard block is only seen by someone standing on the dashboard. This
 * is the number that follows the operator around the app, so "three leads
 * are slipping" is visible while they are deep in a contact profile — which
 * is the whole promise of the never-miss queue.
 *
 * It reads the SAME query the dashboard block uses, with the same default
 * parameters, so the two share one cache entry and one request and can never
 * print different numbers. Rendering nothing at zero is deliberate: a badge
 * that is always there stops being read.
 */
export function NeedsAttentionNavBadge({
  className,
  variant = 'inline',
}: {
  className?: string;
  /**
   * `inline` sits at the end of a sidebar row. `corner` is the tab bar: a
   * small disc pinned to the icon's top-right, sized so it clips the corner
   * rather than covering the icon — which is what the row-shaped pill did
   * when it was dropped on top of a 20px target.
   */
  variant?: 'inline' | 'corner';
}) {
  const query = useNeedsAttention();
  const total = query.data?.total ?? 0;
  if (total === 0) return null;

  const label = total > 9 ? '9+' : String(total);

  if (variant === 'corner') {
    return (
      <span
        title="Open leads that are at risk"
        className={cn(
          'pointer-events-none absolute -right-2 -top-1 grid min-w-[1.05rem] place-items-center',
          'rounded-full bg-danger px-1 text-[10px] font-bold leading-[1.05rem] text-white tabular-nums',
          'ring-2 ring-surface',
          className,
        )}
      >
        {label}
      </span>
    );
  }

  return (
    <Badge tone="danger" className={cn('ml-auto tabular-nums', className)} title="Open leads that are at risk">
      {total > 99 ? '99+' : total}
    </Badge>
  );
}
